<script setup lang="ts">
  import { ref } from "vue";
  import { useTalkRoomStore } from "~/stores/TalkRoomStore";
  import { useSnackBarStore } from "~/stores/SnackBarStore";
  import type { Talk } from "~/entities/Talk";
  import TalkInputMessage from "~/components/Organisms/TalkInputMessage.vue";

  const talkRoomStore = useTalkRoomStore();

  // スナックバーstore
  const snackBarStore = useSnackBarStore();

  interface Props {
    open: boolean;
    talkRoomId: string;
    files: File[];
    replyTalkId?: string;
    threadTalkId: string;
    showInRoom: boolean;
    isTest?: boolean; // テスト時のみ true にする。でないと v-dialog を find できないため
  }
  const props = defineProps<Props>();

  interface Emits {
    (event: "update:open", open: boolean): void;
    (event: "send", value?: Talk[] | boolean): void;
  }
  const emit = defineEmits<Emits>();
  const isOpen = computed<boolean>({
    get: () => props.open,
    set: value => {
      emit("update:open", value);
    },
  });

  const talkInputMessage = ref<InstanceType<typeof TalkInputMessage> | null>(null);

  // 送信可否フラグ
  const canSend = ref<boolean>(false);

  // 送信するファイルリスト
  const filesToSend = ref<File[]>(props.files);
  watch(
    () => props.files,
    () => {
      filesToSend.value = props.files;
    },
  );

  const fileSending = ref<boolean>(false);

  const sendFiles = async (message: string, mentionUserIds: string[]) => {
    const note = filesToSend.value.length === 1 ? message.trim() : "";
    for (const file of filesToSend.value) {
      try {
        await talkRoomStore.sendFile(
          props.talkRoomId!,
          file,
          note,
          mentionUserIds,
          props.replyTalkId,
          props.threadTalkId,
          props.showInRoom,
        );
      } catch (error: any) {
        const lowerFileName = file.name.toLowerCase();
        if (lowerFileName.endsWith(".pdf")) {
          sentryErrorLog(`トークPDFファイル送信失敗 ${error}`);
        } else {
          sentryErrorLog(`トーク画像送信失敗 ${error}`);
        }
        throw error;
      }
    }
  };

  const deleteFile = (index: number) => {
    filesToSend.value.splice(index, 1);
    if (filesToSend.value.length === 0) {
      closeFileDialog();
    }
  };

  const closeFileDialog = () => {
    isOpen.value = false;
  };

  const getObjectUrl = (file: File) => {
    return URL.createObjectURL(file);
  };

  const sendFunc = async (message: string, mentionUserIds: string[]) => {
    await sendFiles(message, mentionUserIds);
  };
  const onSend = () => {
    talkInputMessage.value?.triggerSend();
  };
  const onSent = () => {
    emit("send", true);
    closeFileDialog();
  };
  const onFailed = (error: any) => {
    sentryErrorLog(`トークファイル送信失敗 ${error}`);
    snackBarStore.setError("ファイルの送信に失敗しました");
  };

  // レスポンシブ対応
  const isSp = isSpDisplayRef();
</script>

<template>
  <AtomsOverlay v-model:loading="fileSending" />
  <v-dialog v-model="isOpen" scrim="black" :attach="!!isTest">
    <v-sheet
      class="file-dialog py-8 rounded fileDialog"
      :class="isSp ? 'px-4' : 'px-10'"
      color="monochrome-04"
      align="center"
    >
      <div class="text-dialog-title">ファイルを送信しますか？</div>
      <div class="files">
        <div v-for="(file, fileIndex) in filesToSend" :key="fileIndex" class="file ma-auto">
          <v-icon
            v-if="filesToSend.length > 1"
            small
            icon="$mdiCloseBox"
            data-testid="file-delete"
            @click="deleteFile(fileIndex)"
          ></v-icon>
          <img
            v-if="isImageFile(file)"
            :src="getObjectUrl(file)"
            :class="filesToSend.length === 1 ? `image` : `image-s`"
            alt="thumbnail"
          />
          <div v-if="isPdfFile(file)" :class="filesToSend.length === 1 ? `pdf` : `pdf-s`">{{ file.name }}</div>
        </div>
      </div>

      <OrganismsTalkInputMessage
        v-show="filesToSend.length === 1"
        ref="talkInputMessage"
        v-model:can-send="canSend"
        v-model:is-sending="fileSending"
        class="talkroom_input_note"
        :allow-empty-send="true"
        placeholder="説明を入力..."
        :fixed-position="false"
        :send-func="sendFunc"
        @sent="onSent"
        @failed="onFailed"
      />

      <div class="d-flex justify-space-evenly">
        <AtomsDialogButton
          :disabled="fileSending"
          color="monochrome-02"
          class="mt-5"
          data-testid="file-send-cancel"
          @click="closeFileDialog"
          >キャンセル</AtomsDialogButton
        >
        <AtomsDialogButton
          :loading="fileSending"
          color="main-01"
          class="mt-5"
          data-testid="file-send-ok"
          @click="onSend"
          >送信</AtomsDialogButton
        >
      </div>
    </v-sheet>
  </v-dialog>
</template>

<style lang="scss" scoped>
  @import "assets/media.scss";

  // TODO TalkMessage と類似。コンポーネント化したい
  .file-dialog {
    width: 600px;
    max-width: calc(100% - 40px);
    min-height: 146px;
    min-width: 345px;
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    -webkit-transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
  }
  .fileDialog {
    .files {
      display: flex;
      flex-wrap: wrap;
      margin: 30px 0 10px;
      .file {
        margin-right: 10px;
        i {
          position: absolute;
          top: -15px;
          right: -2px;
        }
        position: relative;
        .image {
          width: 160px;
          height: 160px;
          object-fit: cover;
        }
        .pdf {
          width: 160px;
          height: 160px;
          background-color: gray;
          font-size: 20px;
          vertical-align: middle;
          display: table-cell;
        }
        .image-s {
          width: 80px;
          height: 80px;
          object-fit: cover;
        }
        .pdf-s {
          width: 80px;
          height: 80px;
          background-color: gray;
          font-size: 10px;
          vertical-align: middle;
          display: table-cell;
        }
      }
    }
  }
  .talkroom_input_note {
    display: block;
    width: 100%;
    cursor: auto;
    > div {
      margin: 0 auto;
    }
  }
</style>
